import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminFetch } from '../../lib/api';
import { Building2, Plus, Search, Filter, Upload, Download, ChevronDown, ChevronLeft, ChevronRight, Edit, Trash2, Eye, X, Phone, Mail, Globe, MapPin } from 'lucide-react';

interface Lead {
  id: string;
  name: string;
  city: string;
  state: string;
  postal_code: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  student_count_estimate: number | null;
  status: string;
  priority: string;
  source: string;
  assigned_to: string;
  partner_id: string | null;
  next_action: string;
  next_action_date: string | null;
  estimated_value: number;
  tags: string[];
  notes: string;
  created_at: string;
  sales_partners?: { name: string };
}

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  neu: { label: 'Neu', color: '#8b95a5' },
  kontaktiert: { label: 'Kontaktiert', color: '#3b82f6' },
  interessiert: { label: 'Interessiert', color: '#f59e0b' },
  demo: { label: 'Demo', color: '#8b5cf6' },
  verhandlung: { label: 'Verhandlung', color: '#ec4899' },
  gewonnen: { label: 'Gewonnen', color: '#10b981' },
  verloren: { label: 'Verloren', color: '#ef4444' },
  archiviert: { label: 'Archiviert', color: '#6b7280' },
};

const PRIORITY_MAP: Record<string, { label: string; color: string }> = {
  low: { label: 'Niedrig', color: '#6b7280' },
  medium: { label: 'Mittel', color: '#3b82f6' },
  high: { label: 'Hoch', color: '#f59e0b' },
  urgent: { label: 'Dringend', color: '#ef4444' },
};

const SOURCE_MAP: Record<string, string> = {
  web: 'Website',
  referral: 'Empfehlung',
  event: 'Event',
  cold_outreach: 'Cold Outreach',
  partner: 'Partner',
  linkedin: 'LinkedIn',
  affiliate: 'Affiliate',
  other: 'Sonstige',
};

const EMPTY_LEAD = {
  name: '', city: '', state: '', postal_code: '', contact_person: '', contact_email: '',
  contact_phone: '', website: '', student_count_estimate: null as number | null, status: 'neu',
  priority: 'medium', source: 'cold_outreach', assigned_to: '', estimated_value: 0,
  next_action: '', next_action_date: null as string | null, notes: '',
};

export const SalesLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [form, setForm] = useState({ ...EMPTY_LEAD });
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState<Lead | null>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const LIMIT = 25;

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      const res = await adminFetch(`/api/admin/sales/leads?${params}`);
      const json = await res.json();
      setLeads(json.data || []);
      setTotal(json.total || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, priorityFilter]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = editingLead ? 'PUT' : 'POST';
      const url = editingLead ? `/api/admin/sales/leads/${editingLead.id}` : '/api/admin/sales/leads';
      await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setShowForm(false);
      setEditingLead(null);
      setForm({ ...EMPTY_LEAD });
      loadLeads();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Diesen Lead wirklich archivieren?')) return;
    await adminFetch(`/api/admin/sales/leads/${id}`, { method: 'DELETE' });
    loadLeads();
  };

  const openDetail = async (lead: Lead) => {
    setShowDetail(lead);
    try {
      const res = await adminFetch(`/api/admin/sales/leads/${lead.id}/activities`);
      setActivities(await res.json());
    } catch { setActivities([]); }
  };

  const handleExport = async () => {
    const res = await adminFetch('/api/admin/sales/leads/export');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahryo-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return setImportResults({ imported: 0, errors: ['Datei enthält keine Daten'] });
    
    const headers = lines[0].split(';').map(h => h.trim().replace(/"/g, ''));
    const leads = lines.slice(1).map(line => {
      const values = line.split(';').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => { obj[h] = values[i] || ''; });
      return obj;
    });

    const res = await adminFetch('/api/admin/sales/leads/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ leads }),
    });
    setImportResults(await res.json());
    loadLeads();
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><Building2 style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Fahrschulen</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setShowImport(!showImport)}>
            <Upload size={14} /> Import
          </button>
          <button className="btn btn-ghost" onClick={handleExport}>
            <Download size={14} /> Export
          </button>
          <button className="btn btn-primary" onClick={() => { setForm({ ...EMPTY_LEAD }); setEditingLead(null); setShowForm(true); }}>
            <Plus size={14} /> Neuer Lead
          </button>
        </div>
      </div>

      {/* Import UI */}
      {showImport && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <h3>CSV-Import</h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '8px 0' }}>
            CSV-Datei mit Semikolon-Trennung. Pflichtfelder: <code>name</code>, <code>city</code>.
            Optionale Felder: state, postal_code, contact_person, contact_email, contact_phone, website, student_count_estimate, priority, source, estimated_value, notes.
          </p>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleImportFile} />
          {importResults && (
            <div style={{ marginTop: 12 }}>
              <p style={{ color: '#10b981', fontWeight: 600 }}>✅ {importResults.imported} Leads importiert</p>
              {importResults.errors?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {importResults.errors.map((e: string, i: number) => (
                    <p key={i} style={{ color: '#ef4444', fontSize: 12 }}>⚠️ {e}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text"
            placeholder="Suchen (Name, Stadt, E-Mail...)"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ paddingLeft: 32, width: '100%' }}
          />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}>
          <option value="">Alle Prioritäten</option>
          {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Leads Table */}
      <div className="card" style={{ overflow: 'auto' }}>
        <table className="admin-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Fahrschule</th>
              <th>Stadt</th>
              <th>Ansprechpartner</th>
              <th>Status</th>
              <th>Priorität</th>
              <th>Wert</th>
              <th>Nächste Aktion</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 16, width: '80%' }} /></td>
                  ))}
                </tr>
              ))
            ) : leads.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>Keine Leads gefunden</td></tr>
            ) : (
              leads.map(lead => (
                <tr key={lead.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(lead)}>
                  <td>
                    <strong>{lead.name}</strong>
                    {lead.sales_partners?.name && (
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>
                        via {lead.sales_partners.name}
                      </span>
                    )}
                  </td>
                  <td>{lead.city}{lead.state ? `, ${lead.state}` : ''}</td>
                  <td>
                    {lead.contact_person || '–'}
                    {lead.contact_email && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>{lead.contact_email}</span>}
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      color: '#fff', background: STATUS_MAP[lead.status]?.color || '#666',
                    }}>
                      {STATUS_MAP[lead.status]?.label || lead.status}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500,
                      color: PRIORITY_MAP[lead.priority]?.color || '#666',
                      background: (PRIORITY_MAP[lead.priority]?.color || '#666') + '15',
                    }}>
                      {PRIORITY_MAP[lead.priority]?.label || lead.priority}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {lead.estimated_value ? `${(lead.estimated_value / 100).toLocaleString('de-DE')} €` : '–'}
                  </td>
                  <td style={{ fontSize: 12, maxWidth: 150 }}>
                    {lead.next_action || '–'}
                    {lead.next_action_date && (
                      <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 11 }}>
                        {new Date(lead.next_action_date).toLocaleDateString('de-DE')}
                      </span>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => {
                        setEditingLead(lead);
                        setForm({
                          name: lead.name, city: lead.city, state: lead.state || '', postal_code: lead.postal_code || '',
                          contact_person: lead.contact_person || '', contact_email: lead.contact_email || '',
                          contact_phone: lead.contact_phone || '', website: lead.website || '',
                          student_count_estimate: lead.student_count_estimate, status: lead.status,
                          priority: lead.priority, source: lead.source, assigned_to: lead.assigned_to || '',
                          estimated_value: lead.estimated_value, next_action: lead.next_action || '',
                          next_action_date: lead.next_action_date, notes: lead.notes || '',
                        });
                        setShowForm(true);
                      }} title="Bearbeiten">
                        <Edit size={14} />
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(lead.id)} title="Archivieren">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{total} Leads gesamt</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ padding: '4px 12px', fontSize: 13 }}>Seite {page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Lead Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>{editingLead ? 'Lead bearbeiten' : 'Neuer Lead'}</h2>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 20 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Fahrschulname *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="z.B. Fahrschule Müller" />
              </div>
              <div>
                <label>Stadt *</label>
                <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Berlin" />
              </div>
              <div>
                <label>Bundesland</label>
                <input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} placeholder="Berlin" />
              </div>
              <div>
                <label>PLZ</label>
                <input value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} placeholder="10115" />
              </div>
              <div>
                <label>Ansprechpartner</label>
                <input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} />
              </div>
              <div>
                <label>E-Mail</label>
                <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <div>
                <label>Telefon</label>
                <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
              <div>
                <label>Website</label>
                <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://" />
              </div>
              <div>
                <label>Geschätzte Schülerzahl</label>
                <input type="number" value={form.student_count_estimate ?? ''} onChange={e => setForm({ ...form, student_count_estimate: e.target.value ? parseInt(e.target.value) : null })} />
              </div>
              <div>
                <label>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label>Priorität</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label>Quelle</label>
                <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                  {Object.entries(SOURCE_MAP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label>Geschätzter Wert (Cent)</label>
                <input type="number" value={form.estimated_value} onChange={e => setForm({ ...form, estimated_value: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <label>Zugewiesen an</label>
                <input value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} />
              </div>
              <div>
                <label>Nächste Aktion</label>
                <input value={form.next_action} onChange={e => setForm({ ...form, next_action: e.target.value })} placeholder="Rückruf planen..." />
              </div>
              <div>
                <label>Nächste Aktion Datum</label>
                <input type="datetime-local" value={form.next_action_date?.split('.')[0] || ''} onChange={e => setForm({ ...form, next_action_date: e.target.value ? new Date(e.target.value).toISOString() : null })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Notizen</label>
                <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name || !form.city}>
                {saving ? 'Speichern...' : editingLead ? 'Aktualisieren' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>{showDetail.name}</h2>
              <button className="btn btn-ghost" onClick={() => setShowDetail(null)}><X size={16} /></button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                <InfoRow icon={<MapPin size={14} />} label="Standort" value={`${showDetail.city}${showDetail.state ? `, ${showDetail.state}` : ''} ${showDetail.postal_code || ''}`} />
                <InfoRow icon={<Building2 size={14} />} label="Ansprechpartner" value={showDetail.contact_person || '–'} />
                <InfoRow icon={<Mail size={14} />} label="E-Mail" value={showDetail.contact_email || '–'} />
                <InfoRow icon={<Phone size={14} />} label="Telefon" value={showDetail.contact_phone || '–'} />
                <InfoRow icon={<Globe size={14} />} label="Website" value={showDetail.website || '–'} />
                <InfoRow label="Schülerzahl" value={showDetail.student_count_estimate?.toString() || '–'} />
                <InfoRow label="Status" value={STATUS_MAP[showDetail.status]?.label || showDetail.status} />
                <InfoRow label="Priorität" value={PRIORITY_MAP[showDetail.priority]?.label || showDetail.priority} />
                <InfoRow label="Quelle" value={SOURCE_MAP[showDetail.source] || showDetail.source} />
                <InfoRow label="Geschätzter Wert" value={showDetail.estimated_value ? `${(showDetail.estimated_value / 100).toLocaleString('de-DE')} €` : '–'} />
              </div>
              {showDetail.notes && (
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ marginBottom: 8 }}>Notizen</h4>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{showDetail.notes}</p>
                </div>
              )}
              <h4 style={{ marginBottom: 12 }}>Aktivitäten ({activities.length})</h4>
              {activities.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Keine Aktivitäten vorhanden</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activities.map((a: any) => (
                    <div key={a.id} style={{
                      padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8,
                      display: 'flex', justifyContent: 'space-between', fontSize: 13,
                    }}>
                      <div>
                        <strong>{a.title}</strong>
                        {a.description && <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>{a.description}</p>}
                      </div>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {new Date(a.performed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow: React.FC<{ icon?: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 14 }}>{value}</div>
  </div>
);
